import * as React from 'react';

export type LegendEntryPattern = 'solid' | 'dashed' | 'dotted';

export interface LegendEntry {
  id: string;
  label: string;
  color: string;
  /** Render as a line swatch (transport layers) instead of a dot (media types). */
  variant?: 'dot' | 'line';
  pattern?: LegendEntryPattern;
  borderColor?: string;
  bgColor?: string;
  textColor?: string;
}

export interface MapLayerLegendProps {
  entries: LegendEntry[];
}

function LineSwatch({ entry }: { entry: LegendEntry }) {
  const style: React.CSSProperties =
    entry.pattern === 'dashed'
      ? {
          backgroundImage: `linear-gradient(to right, ${entry.color} 65%, transparent 65%)`,
          backgroundSize: '10px 2px',
        }
      : entry.pattern === 'dotted'
        ? {
            backgroundImage: `linear-gradient(to right, ${entry.color} 70%, transparent 70%)`,
            backgroundSize: '12px 2px',
          }
        : { backgroundColor: entry.color };

  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5"
      style={{
        borderColor: entry.borderColor,
        backgroundColor: entry.bgColor,
        color: entry.textColor,
      }}
    >
      <span className="h-0.5 w-5" style={style} />
      {entry.label}
    </span>
  );
}

export function MapLayerLegend({ entries }: MapLayerLegendProps) {
  if (!entries || entries.length === 0) return null;

  return (
    <div
      className="absolute bottom-4 left-4 z-10 max-w-[400px] rounded-lg border border-border/50 bg-background/88 px-2.5 py-2 shadow-sm backdrop-blur-sm"
      data-testid="map-legend"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-foreground">
        {entries.map((entry) =>
          entry.variant === 'line' ? (
            <LineSwatch key={entry.id} entry={entry} />
          ) : (
            <span key={entry.id} className="inline-flex items-center gap-1 whitespace-nowrap">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.label}
            </span>
          ),
        )}
      </div>
    </div>
  );
}
